import React from 'react';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getBreadcrumbStructuredData } from '../utils/structuredData';

const Admission = () => {
  const breadcrumbs = [
    { name: "Home", url: "https://pakschoolandcollege.com/" },
    { name: "Admission", url: "https://pakschoolandcollege.com/admission" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Admission - PAK School & College System | Join Our Community of Excellence"
        description="Apply for admission to PAK School & College System. Join our community of excellence and start your journey to success. Quality education from nursery to college level."
        keywords="School Admission, Apply to School, Admission Process, School Enrollment, Student Registration, Academic Programs, Quality Education"
        url="/admission"
        image="/assets/hero-banner.jpg"
        structuredData={getBreadcrumbStructuredData(breadcrumbs)}
        canonical="/admission"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 text-white py-20 px-4 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
          <div className="absolute top-32 right-20 w-16 h-16 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-32 right-1/3 w-24 h-24 border-2 border-white rounded-full"></div>
      </div>

        <div className="relative max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-block bg-white bg-opacity-25 backdrop-blur-sm px-6 py-3 rounded-full mb-6 border border-white border-opacity-30">
              <span className="text-sm font-medium text-white">🎓 Start Your Journey</span>
                </div>
              </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight text-white">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">Admission</span>
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed font-light">
            Join our community of excellence and start your journey to success
          </p>
          <div className="mt-12 flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Why Choose Us
          </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Why Choose PAK School & College System?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              At PAK School & College System, we believe that every student has the potential to shine. 
              Our comprehensive educational approach combines academic excellence with character building.
            </p>
            </div>
            
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: '🎓', title: 'Academic Excellence', desc: 'Rigorous curriculum and expert teaching' },
                  { icon: '🌟', title: 'Character Development', desc: 'Building values and moral foundation' },
                  { icon: '🏫', title: 'Modern Facilities', desc: 'State-of-the-art learning environment' },
                  { icon: '👨‍🏫', title: 'Expert Faculty', desc: 'Experienced and dedicated teachers' }
                ].map((feature, index) => (
                  <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300">
                    <div className="text-3xl mb-4">{feature.icon}</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-8 rounded-3xl">
                <img 
                  src="/assets/mix-images/2-1024x768.jpg" 
                  alt="School Environment" 
                  className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admission Process Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Simple Steps
          </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Admission Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple steps to join our educational family
            </p>
            </div>
            
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                number: '01', 
                title: 'Application', 
                desc: 'Submit your application form with required documents',
                icon: '📝',
                color: 'from-blue-500 to-blue-600'
              },
              { 
                number: '02', 
                title: 'Assessment', 
                desc: 'Student assessment and interview process',
                icon: '📊',
                color: 'from-green-500 to-green-600'
              },
              { 
                number: '03', 
                title: 'Approval', 
                desc: 'Admission approval and fee structure discussion',
                icon: '✅',
                color: 'from-purple-500 to-purple-600'
              },
              { 
                number: '04', 
                title: 'Enrollment', 
                desc: 'Complete enrollment and begin your journey',
                icon: '🎓',
                color: 'from-orange-500 to-orange-600'
              }
            ].map((step, index) => (
              <div key={index} className="group">
                <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center text-2xl mb-4 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                    {step.icon}
              </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-400 mb-2">{step.number}</div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Information Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-purple-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Our Locations
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Campuses</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the campus that's right for you
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Par Hoti Campus',
                badge: 'Main Campus',
                badgeColor: 'bg-blue-600',
                location: 'Nehr Chowk, Swabi Road Mardan',
                contact: '0343-0001144/55/66/77',
                programs: 'All Grades Available',
                icon: '🏫',
                color: 'from-blue-500 to-blue-600'
              },
              {
                name: 'DHA Campus',
                badge: '27 Years',
                badgeColor: 'bg-green-600',
                location: 'Muqam Chowk',
                contact: '0343-0001133',
                programs: 'Play Group to Grade 5',
                icon: '🌟',
                color: 'from-green-500 to-green-600'
              },
              {
                name: 'SMT Campus',
                badge: 'Modern',
                badgeColor: 'bg-purple-600',
                location: 'Phase-2 Sector N, House No.1',
                contact: '0343-0001122',
                programs: 'All Grades Available',
                icon: '✨',
                color: 'from-purple-500 to-purple-600'
              }
            ].map((campus, index) => (
              <div key={index} className="group">
                <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${campus.color} rounded-xl flex items-center justify-center text-xl`}>
                      {campus.icon}
                    </div>
                    <span className={`${campus.badgeColor} text-white px-3 py-1 rounded-full text-xs font-medium`}>
                      {campus.badge}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{campus.name}</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="text-gray-400 mt-1">📍</span>
                  <div>
                        <p className="text-sm font-medium text-gray-700">Location</p>
                        <p className="text-sm text-gray-600">{campus.location}</p>
                  </div>
                </div>
                    
                    <div className="flex items-start space-x-3">
                      <span className="text-gray-400 mt-1">📞</span>
                  <div>
                        <p className="text-sm font-medium text-gray-700">Contact</p>
                        <p className="text-sm text-gray-600">{campus.contact}</p>
                  </div>
                </div>
                    
                    <div className="flex items-start space-x-3">
                      <span className="text-gray-400 mt-1">🎓</span>
                  <div>
                        <p className="text-sm font-medium text-gray-700">Programs</p>
                        <p className="text-sm text-gray-600">{campus.programs}</p>
                      </div>
                  </div>
                </div>
              </div>
            </div>
            ))}
                </div>
                </div>
      </section>

      {/* Contact & Form Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-4">
              Get Started
                </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Ready to Apply?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Contact us today to learn more about our admission process and schedule a campus visit. 
              Our admission team is here to help you every step of the way.
            </p>
                </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">Contact Information</h3>
              
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { icon: '📞', title: 'Call Us', info: '0343-0001144/55/66/77', color: 'from-blue-500 to-blue-600' },
                  { icon: '📧', title: 'Email Us', info: 'info@pakschoolandcollege.com', color: 'from-green-500 to-green-600' },
                  { icon: '📍', title: 'Visit Us', info: 'Nehr Chowk, Swabi Road Mardan', color: 'from-purple-500 to-purple-600' }
                ].map((contact, index) => (
                  <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className={`w-16 h-16 bg-gradient-to-br ${contact.color} rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4`}>
                      {contact.icon}
                </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{contact.title}</h4>
                    <p className="text-sm text-gray-600">{contact.info}</p>
                </div>
                ))}
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Admission;
